import type { LoginResponse } from '@greatsumini/react-facebook-login';
import ReactFacebookLogin from '@greatsumini/react-facebook-login';
import { useFetcher } from '@remix-run/react';
import { useCallback, useEffect } from 'react';
import type { FetcherData } from 'types';

import FacebookIcon from '~/assets/icons/Facebook';
import useEnv from '~/hooks/useEnv';
import useUser from '~/hooks/useUser';
import braze from '~/services/braze.client';
import gtm, { LoginSource } from '~/services/gtm.client';

import type { PostAuthOptions, ScreenProps } from '../..';

type Props = {
  onSuccess: Function;
  postAuthOptions?: PostAuthOptions;
  setError: ScreenProps['setError'];
};

const FacebookLoginButton = ({
  onSuccess,
  postAuthOptions,
  setError
}: Props) => {
  const fetcher = useFetcher<FetcherData>();
  const user = useUser();
  const env = useEnv();

  const errors = fetcher.data?.errors;

  function onFacebookLoginSuccess(res: LoginResponse['authResponse']) {
    fetcher.submit(
      {
        action: postAuthOptions?.action ?? '',
        guestId: user.guestId ?? '',
        legacyProductId: postAuthOptions?.legacyProductId ?? '',
        redirectUrl: postAuthOptions?.redirectUrl ?? '',
        sizes: JSON.stringify(postAuthOptions?.sizes ?? []),
        token: res?.accessToken ?? ''
      },
      { action: '/bolt/login/facebook', method: 'post' }
    );

    gtm.trackedButtonClick({
      buttonName: 'SIGN_IN_FACEBOOK',
      pagePosition: 'Sign in modal'
    });
  }

  const onSignIn = useCallback(() => {
    gtm.sendLoginEvent(user.id, LoginSource.Meta);

    braze.setUser(user.id);
    braze.logLoginEvent();

    onSuccess();
  }, [onSuccess, user.id]);

  useEffect(() => {
    if (errors?.message) setError(errors.message);
  }, [errors, setError]);

  useEffect(() => {
    if (fetcher.data?.success) onSignIn();
  }, [fetcher.data, onSignIn]);

  const isLoggingIn = fetcher.state === 'submitting';

  return (
    <div className="flex h-12 w-full items-center justify-center rounded-lg bg-concrete px-4 py-2 text-black">
      <ReactFacebookLogin
        appId={env.FACEBOOK_APP_ID}
        onSuccess={onFacebookLoginSuccess}
        fields="id,email"
        onFail={() =>
          setError(
            'Facebook sign in failed. If on a social media browser, please open this page in your external browser. If not, please try again or use another method.'
          )
        }
        style={{
          alignItems: 'center',
          display: 'flex',
          fontSize: '16px',
          fontWeight: '600',
          gap: '10px',
          justifyContent: 'center',
          pointerEvents: isLoggingIn ? 'none' : 'auto',
          width: '100%'
        }}
      >
        {isLoggingIn ? (
          'Signing in...'
        ) : (
          <>
            <FacebookIcon height="24" width="24" />
            <span className="hidden md:inline-block">Facebook</span>
          </>
        )}
      </ReactFacebookLogin>
    </div>
  );
};

export default FacebookLoginButton;
